// Official alerts (NWS in the US, ECCC in Canada) — shown apart from this app's calculated rule
// triggers, with an "Official" badge, so a platform-derived signal is never
// mistaken for an issued warning (or the other way round). An unavailable
// feed is always shown as unavailable, never as "no alerts".

const SEVERITY_CLASS = { Extreme: 'sev-3', Severe: 'sev-3', Moderate: 'sev-2', Minor: 'sev-1' };

function fmtTime(iso, timeZone) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    timeZone: timeZone || undefined,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

const nwsPageUrl = (lat, lon) =>
  `https://forecast.weather.gov/MapClick.php?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}`;

const PROVIDERS = {
  NWS: { name: 'National Weather Service', short: 'NWS', home: 'https://www.weather.gov/', homeLabel: 'weather.gov' },
  ECCC: {
    name: 'Environment and Climate Change Canada',
    short: 'ECCC',
    home: 'https://weather.gc.ca/warnings/index_e.html',
    homeLabel: 'weather.gc.ca',
  },
};
/** Provider of a hazards block or alert ('NWS' when unknown, as before). */
export const providerOf = (x) => PROVIDERS[x?.provider ?? x?.feeds?.[0]?.provider] ?? PROVIDERS.NWS;

export function AlertCard({ alert, timeZone, point, compact = false }) {
  const until = alert.ends ?? alert.expires;
  return (
    <div className={`rule official ${SEVERITY_CLASS[alert.severity] ?? 'sev-1'}`}>
      <div className="rule-head">
        <span className="rule-title">
          <span className="badge-official">Official</span> {alert.event}
        </span>
        <span className="rule-cite">
          {alert.senderName ?? providerOf(alert).name} · {alert.severity}
        </span>
      </div>
      <div className="rule-tags">
        {fmtTime(alert.sent, timeZone) && <span className="tag">Issued {fmtTime(alert.sent, timeZone)}</span>}
        {fmtTime(until, timeZone) && <span className="tag">Until {fmtTime(until, timeZone)}</span>}
        {alert.match === 'polygon' && <span className="tag">Inside warning polygon</span>}
        {alert.match === 'zone' && <span className="tag">Issued for this county/zone</span>}
        {alert.match === 'buffer' && (
          <span className="tag tag-planning">
            Outside the warning polygon, {alert.distanceM != null ? `${alert.distanceM} m` : 'within buffer'} from its edge
          </span>
        )}
        {compact && alert.zipsAffected != null && (
          <span className="tag">
            {alert.zipsAffected === 0
              ? 'Touches county; no ZIP centroid inside'
              : `${alert.zipsAffected} ZIP${alert.zipsAffected === 1 ? '' : 's'}`}
          </span>
        )}
      </div>
      {alert.headline && <div className="rule-detail">{alert.headline}</div>}
      {!compact && alert.instruction && (
        <div className="rule-detail alert-instruction">
          <strong>{providerOf(alert).short} instructions:</strong> {alert.instruction}
        </div>
      )}
      {!compact && alert.provider === 'ECCC' && alert.description && (
        <div className="rule-detail alert-instruction" style={{ whiteSpace: 'pre-line' }}>
          {alert.description}
        </div>
      )}
      <div className="rule-detail alert-links">
        {point && alert.provider !== 'ECCC' && (
          <a href={nwsPageUrl(point.lat, point.lon)} target="_blank" rel="noreferrer">
            NWS page for this location
          </a>
        )}
        {alert.webUrl && (
          <a href={alert.webUrl} target="_blank" rel="noreferrer">
            ECCC alerts for this province
          </a>
        )}
        {alert.sourceUrl && (
          <a href={alert.sourceUrl} target="_blank" rel="noreferrer">
            Source alert ({providerOf(alert).short} API)
          </a>
        )}
      </div>
    </div>
  );
}

function checkedAt(hazards, timeZone) {
  const t = hazards?.feeds?.[0]?.lastSuccess;
  return t ? fmtTime(t, timeZone) : null;
}

/**
 * No official feed is connected for this area (Canada: NWS is US-only).
 * Never shown as "no alerts".
 */
function NotCoveredNote({ banner = false }) {
  const body = (
    <>
      No official alert feed is connected for this area, so the absence of alerts here is <strong>not</strong> an
      all-clear. Check the local weather authority directly.
    </>
  );
  return banner ? (
    <div className="banner warn">
      <strong>No official alert feed for this area yet.</strong> {body}
    </div>
  ) : (
    <div className="rule sev-1">
      <div className="rule-title">No official alert feed connected for this area</div>
      <div className="rule-detail">{body}</div>
    </div>
  );
}

/** Per-ZIP official alerts section for the detail panel. */
export function ZipOfficialAlerts({ zip }) {
  const h = zip.hazards;
  if (h?.status === 'not-covered') {
    return (
      <>
        <h3 style={{ marginTop: 18 }}>Official alerts</h3>
        <NotCoveredNote />
      </>
    );
  }
  const alerts = zip.officialAlerts;
  const isSite = zip.zip === 'site'; // saved site (exact coordinates), not a ZIP centroid
  const point = { lat: zip.lat, lon: zip.lon };
  const pv = zip.country === 'CA' ? PROVIDERS.ECCC : providerOf(h);
  const where = zip.kind === 'municipality' ? 'this municipality’s point' : 'this ZIP’s location';
  return (
    <>
      <h3 style={{ marginTop: 18 }}>Official alerts — {pv.name}</h3>
      {(!h || h.status === 'unavailable') && (
        <div className="rule sev-2 official-unavailable">
          <div className="rule-title">{pv.short} alert feed unavailable</div>
          <div className="rule-detail">
            Official alerts couldn&rsquo;t be retrieved, so this is <strong>not</strong> an
            all-clear. Check{' '}
            <a href={pv.short === 'NWS' ? nwsPageUrl(zip.lat, zip.lon) : pv.home} target="_blank" rel="noreferrer">
              {pv.homeLabel}
            </a>{' '}
            directly.
          </div>
        </div>
      )}
      {h?.status === 'delayed' && (
        <div className="rule-detail feed-note">
          Feed delayed: showing alerts last retrieved {checkedAt(h, zip.timezone) ?? 'earlier'}. They
          may be out of date.
        </div>
      )}
      {h && h.status !== 'unavailable' && alerts?.length === 0 && (
        <div className="rule sev-0">
          <div className="rule-title">
            {isSite ? `No active ${pv.short} alerts at this site` : `No active ${pv.short} alerts at ${where}`}
          </div>
          <div className="rule-detail">
            {isSite
              ? `Checked at the site's coordinates${h.bufferM ? ` with a ${h.bufferM} m buffer` : ''}`
              : zip.kind === 'municipality'
                ? 'Checked at a point inside the municipality'
                : 'Checked at the ZIP centroid'}
            {checkedAt(h, zip.timezone) ? `, ${checkedAt(h, zip.timezone)}` : ''}.
            {isSite ? '' : ' Conditions at your exact site can differ.'}
          </div>
        </div>
      )}
      {alerts?.map((a) => (
        <AlertCard key={a.id} alert={a} timeZone={zip.timezone} point={point} />
      ))}
    </>
  );
}

/** County-level panel shown above the table. */
export function CountyOfficialAlerts({ hazards, timeZone }) {
  if (hazards?.status === 'not-covered') return <NotCoveredNote banner />;
  const pv = providerOf(hazards);
  if (!hazards || hazards.status === 'unavailable') {
    return (
      <div className="banner err">
        <strong>Official {pv.short} alerts unavailable.</strong> The alert feed couldn&rsquo;t be reached,
        so the absence of alerts here is <strong>not</strong> an all-clear. Check{' '}
        <a href={pv.home} target="_blank" rel="noreferrer">
          {pv.homeLabel}
        </a>{' '}
        directly.
      </div>
    );
  }
  const stamp = checkedAt(hazards, timeZone);
  return (
    <section className="official-panel">
      <div className="official-panel-head">
        <h3>
          Official alerts <span className="muted">— {pv.name}</span>
        </h3>
        <span className={`feed-pill feed-${hazards.status}`}>
          {hazards.status === 'delayed' ? 'Feed delayed' : 'Feed current'}
          {stamp ? ` · ${stamp}` : ''}
        </span>
      </div>
      {hazards.status === 'delayed' && (
        <div className="rule-detail feed-note">
          The latest refresh failed; these are the last alerts retrieved and may be out of date.
        </div>
      )}
      {hazards.alerts.length === 0 ? (
        <div className="rule-detail">No active {pv.short} alerts for this area.</div>
      ) : (
        hazards.alerts.map((a) => <AlertCard key={a.id} alert={a} timeZone={timeZone} compact />)
      )}
    </section>
  );
}

/**
 * Join each ZIP's alert ids to the county's full alert objects, so table
 * and detail components can read `zip.officialAlerts` directly.
 */
export function attachOfficialAlerts(snap) {
  if (!snap) return snap;
  const byId = new Map((snap.hazards?.alerts ?? []).map((a) => [a.id, a]));
  return {
    ...snap,
    zips: snap.zips.map((z) => ({
      ...z,
      officialAlerts:
        z.hazards?.alerts == null
          ? null
          : z.hazards.alerts.map((m) => byId.get(m.id) && { ...byId.get(m.id), match: m.match }).filter(Boolean),
    })),
  };
}
